import { trackFileUploaded } from '#/analytics/trackFileUploaded';
import { useApiClientFetchS3FileMeta } from '#/api/s3File/apiFetchS3UploadMeta';
import { apiUploadFileToS3 } from '#/api/s3File/apiUploadFileToS3';
import type { ApiFileUploadType } from '#/api/types/ApiFileUploadType';
import type { ApiS3FileId } from '#/api/types/ApiTypedId';
import { useUser } from '#/contexts/UserEnvironmentContext';

import { useFileUpload } from './useFileUpload';

type Args = {
  fileId: ApiS3FileId | null;
  fileType: ApiFileUploadType;
  onChange: (newFileId: ApiS3FileId | null) => void;
  onError: (error: Error) => void;
  canRemove: boolean;
  maxSizeInMB: number | null;
  extraAnalyticsData: Record<string, unknown> | null;
};

export function useS3FileUpload({
  fileId,
  fileType,
  onChange,
  onError,
  canRemove,
  maxSizeInMB,
  extraAnalyticsData,
}: Args) {
  const [fileMeta] = useApiClientFetchS3FileMeta(fileId ?? null);

  const { user } = useUser();

  const { fileUploadStateAndActions } = useFileUpload({
    fileId,
    fileMeta: fileMeta ?? null,
    onChange,
    onError,
    canRemove,
    maxSizeInMB,
    uploadFileToS3: async (newFile) => {
      const newFileId = await apiUploadFileToS3({ file: newFile, fileType });

      trackFileUploaded({
        file: newFile,
        fileType,
        user,
        extraAnalyticsData,
      });

      return newFileId;
    },
  });

  return { fileUploadStateAndActions };
}
